"use client";

import { useRef, useEffect, useState } from "react";
import Link from "next/link";
import { motion, useScroll, useTransform, useSpring, useAnimation } from "framer-motion";
import { Sparkles, Code2, Zap, ArrowDown } from "lucide-react";
import { Aurora } from "../backgrounds/Aurora";
import { MagneticButton } from "../interactions/MagneticButton";
import { useReducedMotion } from "@/hooks/useReducedMotion";

export function HeroSection() {
  const ref = useRef<HTMLElement>(null);
  const reducedMotion = useReducedMotion();
  const controls = useAnimation();
  const [hasAnimated, setHasAnimated] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 150]);

  useEffect(() => {
    if (!hasAnimated) {
      controls.start("visible");
      setHasAnimated(true);
    }
  }, [controls, hasAnimated]);

  const letterVariants = {
    hidden: { y: 100, opacity: 0, rotateX: -90 },
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      rotateX: 0,
      transition: {
        delay: 0.5 + i * 0.05,
        duration: 0.8,
        ease: [0.215, 0.61, 0.355, 1] as const,
      },
    }),
  };

  const title1 = "BUILD AT";
  const title2 = "WARP SPEED";

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Vibrant Aurora Background */}
      <Aurora
        colors={["#7c3aed", "#2563eb", "#06b6d4", "#ec4899", "#f59e0b"]}
        speed={0.7}
        blur={80}
        intensity={0.5}
      />

      {/* Animated grid lines */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(124, 58, 237, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(124, 58, 237, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <motion.nav
        className="fixed top-0 z-50 w-full border-b border-white/10 bg-[#252423]/60 backdrop-blur-xl"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
      >
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <motion.div
            className="flex items-center gap-3"
            whileHover={{ scale: 1.05 }}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 shadow-lg shadow-violet-500/25">
              <Code2 className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-wider bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">VIBE</span>
          </motion.div>
          <div className="flex items-center gap-6">
            <Link
              href="/auth/login"
              className="text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              Login
            </Link>
            <Link href="/auth/register">
              <motion.button
                className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-600 rounded-xl text-sm font-semibold text-white shadow-lg shadow-violet-500/25"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(124, 58, 237, 0.4)" }}
                whileTap={{ scale: 0.98 }}
              >
                Get Started
              </motion.button>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Content */}
      <motion.div
        className="relative z-10 flex flex-col items-center text-center px-4 pt-16"
        style={reducedMotion ? {} : { opacity, scale, y }}
      >
        {/* Glowing badge */}
        <motion.div
          className="relative mb-8"
          initial={{ opacity: 0, y: 30, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-cyan-600 rounded-full blur-xl opacity-50" />
          <div className="relative flex items-center gap-2 px-5 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-white/90">Next-Gen AI Software Factory</span>
            <Sparkles className="w-4 h-4 text-cyan-400" />
          </div>
        </motion.div>

        {/* Main Headline with 3D letter animation */}
        <div className="mb-6" style={{ perspective: "1000px" }}>
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black tracking-tighter leading-none">
            {/* BUILD AT */}
            <span className="block overflow-hidden">
              <motion.span className="flex justify-center">
                {title1.split("").map((letter, i) => (
                  <motion.span
                    key={i}
                    custom={i}
                    variants={letterVariants}
                    initial="hidden"
                    animate={controls}
                    className="inline-block bg-gradient-to-b from-white via-white to-white/40 bg-clip-text text-transparent"
                    style={{
                      textShadow: "0 0 80px rgba(255,255,255,0.5)",
                      transformStyle: "preserve-3d"
                    }}
                  >
                    {letter === " " ? "\u00A0" : letter}
                  </motion.span>
                ))}
              </motion.span>
            </span>

            {/* WARP SPEED */}
            <span className="block overflow-hidden mt-2">
              <motion.span className="flex justify-center">
                {title2.split("").map((letter, i) => (
                  <motion.span
                    key={i}
                    custom={i + title1.length}
                    variants={letterVariants}
                    initial="hidden"
                    animate={controls}
                    className="inline-block bg-gradient-to-r from-violet-400 via-cyan-400 to-pink-400 bg-clip-text text-transparent"
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    {letter === " " ? "\u00A0" : letter}
                  </motion.span>
                ))}
              </motion.span>
            </span>
          </h1>
        </div>

        {/* Subtitle with blur reveal */}
        <motion.p
          className="max-w-2xl mx-auto text-lg md:text-xl text-white/60 font-light leading-relaxed mb-12"
          initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          Vibe uses advanced AI agents to plan, verify, and implement complex systems.
          Experience the future of pair programming with deep context awareness.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row items-center gap-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.6 }}
        >
          <Link href="/auth/register">
            <motion.button
              className="group relative px-10 py-4 bg-gradient-to-r from-violet-600 via-purple-600 to-cyan-600 rounded-2xl text-lg font-bold text-white overflow-hidden"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="relative z-10 flex items-center gap-2">
                START BUILDING
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  →
                </motion.span>
              </span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-violet-600"
                initial={{ x: "100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
          </Link>

          <Link href="#demo">
            <motion.button
              className="px-8 py-4 border border-white/20 rounded-2xl text-lg font-medium text-white/80 hover:bg-white/10 hover:border-white/40 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Watch Demo
            </motion.button>
          </Link>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="flex flex-wrap justify-center gap-8 mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 0.6 }}
        >
          {[
            { value: "10x", label: "Faster Development" },
            { value: "50K+", label: "Lines Generated" },
            { value: "99%", label: "Accuracy Rate" },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8 + i * 0.1, duration: 0.5 }}
            >
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-sm text-white/40 mt-1">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 0.5 }}
      >
        <motion.div
          className="flex flex-col items-center gap-2 cursor-pointer"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          onClick={() => document.getElementById('problem')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <span className="text-xs uppercase tracking-widest text-white/40">Scroll to explore</span>
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
            <motion.div
              className="w-1.5 h-1.5 bg-white/60 rounded-full"
              animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

export default HeroSection;
